import type { CSSProperties } from 'react';
import type { ProjectionRevote, ProjectionStep } from '../classroom/classroomApi';
import {
  barHeightVh, barWidthPercent, columnize, countsByOptionIndex, percentOf,
} from './projectionLayout';
import { COUNT_ON_BAR_STYLE, OF_LINE_STYLE, optionLabelStyle } from './projectionScale';

// WIP-011 (design §2.2–§2.4, §2.5) — the "results shown" wall: bars for a
// plain poll, or the round-1/round-2 comparison for a revote. Two
// non-negotiables live here, by construction, not by review:
//  - options render in AUTHORED order, always — never sorted by count. A
//    revote where the room converges on the WRONG option must look exactly
//    like one that converges on the right one.
//  - every bar is `bg-ws-accent` (round 2 / single poll) or the ink-2 outline
//    (round 1) — no per-option colour, border, icon, or weight change.
//    Nothing here can be told apart from anything else by an option's index.
function formatAnswered(count: number, expected: number | null): string {
  return expected != null ? `${count} of ${expected} answered` : `${count} answered`;
}

interface BarTrackProps {
  height: number;
  count: number;
  maxCount: number;
  totalForPercent: number;
  variant: 'solid' | 'ghost';
}

/** One bar: a track (always `bg-ws-surface-2`, decorative) plus either the
 * solid accent fill or the round-1 ghost outline, and its count to the
 * right — never drawn on top of the fill (design §2.4: white-on-accent is
 * 2.8:1 in dark; ink-on-surface here is ≥14:1 in every theme). */
function BarTrack({ height, count, maxCount, totalForPercent, variant }: BarTrackProps) {
  const pct = percentOf(count, totalForPercent);
  const widthPct = barWidthPercent(count, maxCount);
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5">
      <div className="relative min-w-0 flex-1 overflow-hidden rounded-md bg-ws-surface-2" style={{ height: `${height}vh` }}>
        {variant === 'solid' ? (
          <div className="absolute inset-y-0 left-0 rounded-md bg-ws-accent" style={{ width: `${widthPct}%` }} />
        ) : (
          <div
            className="absolute inset-y-0 left-0 rounded-md border-[3px] border-ws-ink-2 bg-transparent"
            style={{ width: `${widthPct > 0 ? Math.max(widthPct, 4) : 0}%` }}
          />
        )}
      </div>
      <span style={COUNT_ON_BAR_STYLE} className="w-[9ch] shrink-0 text-right text-ws-ink">
        {count}
        {pct != null && <span style={OF_LINE_STYLE} className="ml-1 text-ws-ink-2">{pct}%</span>}
      </span>
    </div>
  );
}

interface ProjectionBarsProps {
  step: ProjectionStep;
  choices: Record<string, number> | undefined;
  count: number;
  expected: number | null;
  revote?: ProjectionRevote;
}

export default function ProjectionBars({ step, choices, count, expected, revote }: ProjectionBarsProps) {
  const options = step.options || [];
  const indexed = options.map((label, i) => ({ label, i }));
  const columns = columnize(indexed);
  const rowsInColumn = Math.max(...columns.map((c) => c.length), 1);
  const height = barHeightVh(rowsInColumn);
  const labelStyle: CSSProperties = optionLabelStyle(options.length);

  const afterCounts = countsByOptionIndex(choices, options.length);
  const beforeCounts = revote ? countsByOptionIndex(revote.before, options.length) : null;

  // The comparison scales both rounds against ONE shared maximum, so a bar's
  // length says something about the other round too — the point of the
  // second vote is to see the room move toward (or away from) consensus,
  // not just re-read two unrelated charts.
  const maxCount = Math.max(1, ...afterCounts, ...(beforeCounts || []));
  const noAnswersYet = revote ? revote.both === 0 && count === 0 : count === 0;

  return (
    <div className="flex w-full flex-col gap-3">
      <div className={columns.length > 1 ? 'grid grid-cols-2 gap-x-8' : undefined}>
        {columns.map((column, ci) => (
          <div key={ci} className="flex flex-col gap-2.5">
            {column.map(({ label, i }) => (
              <div key={i} className="flex items-center gap-3">
                <span
                  title={label}
                  style={labelStyle}
                  className="w-[24%] shrink-0 text-ws-ink [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] overflow-hidden"
                >
                  {label}
                </span>
                {beforeCounts ? (
                  <>
                    <BarTrack height={height} count={beforeCounts[i]} maxCount={maxCount} totalForPercent={revote?.both ?? 0} variant="ghost" />
                    <BarTrack height={height} count={afterCounts[i]} maxCount={maxCount} totalForPercent={count} variant="solid" />
                  </>
                ) : (
                  <BarTrack height={height} count={afterCounts[i]} maxCount={maxCount} totalForPercent={count} variant="solid" />
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="mt-2 flex items-center justify-between" style={OF_LINE_STYLE}>
        {revote ? (
          <>
            <span className="text-ws-ink-2">
              {noAnswersYet ? 'No answers yet' : `${revote.changed} of ${revote.both} changed their answer`}
            </span>
            <span className="text-ws-ink-2">{formatAnswered(count, expected)}</span>
          </>
        ) : (
          <span className="text-ws-ink-2">{noAnswersYet ? 'No answers yet' : formatAnswered(count, expected)}</span>
        )}
      </div>
    </div>
  );
}
